import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TableRestaurant as TableIcon,
} from '@mui/icons-material';
import { tableAPI } from '../services/api';

const TablesPage = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (shopId) {
      fetchTables();
    }
  }, [shopId]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await tableAPI.getTables(shopId);
      setTables(response.data || []);
    } catch (error) {
      console.error('Error fetching tables:', error);
      setError(error.response?.data?.message || 'Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'occupied':
        return 'error';
      case 'reserved':
        return 'warning';
      case 'unavailable':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/shops')}
          sx={{ mb: 2 }}
        >
          Back to Shops
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2} 
        mb={3}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/shops')}
          size={isMobile ? "medium" : "large"}
          fullWidth={isMobile}
        >
          {isMobile ? 'Back' : 'Back to Shops'}
        </Button>
        <Typography variant={isMobile ? "h5" : "h4"}>Tables</Typography>
      </Box>

      {tables.length === 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
          <TableIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" gutterBottom>
            No tables found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This shop doesn't have any tables yet.
          </Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="success.main">
                  {tables.filter((t) => t.status === 'available').length}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontSize={isMobile ? "0.75rem" : "0.875rem"}>
                  Available
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="error.main">
                  {tables.filter((t) => t.status === 'occupied').length}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontSize={isMobile ? "0.75rem" : "0.875rem"}>
                  Occupied
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="warning.main">
                  {tables.filter((t) => t.status === 'reserved').length}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontSize={isMobile ? "0.75rem" : "0.875rem"}>
                  Reserved
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Typography variant={isMobile ? "h5" : "h4"}>
                  {tables.length}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontSize={isMobile ? "0.75rem" : "0.875rem"}>
                  Total Tables
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {tables.map((table) => (
                <Card key={table._id} sx={{ width: '100%' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight="medium">
                          Table {table.tableNumber}
                        </Typography>
                        {table.tableName && (
                          <Typography variant="body2" color="text.secondary">
                            {table.tableName}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={table.status}
                        size="small"
                        color={getStatusColor(table.status)}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>

                    <Stack spacing={1.5}>
                      {table.capacity && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Capacity
                          </Typography>
                          <Typography variant="body2">{table.capacity} seats</Typography>
                        </Box>
                      )}

                      {table.location && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Location
                          </Typography>
                          <Typography variant="body2">{table.location}</Typography>
                        </Box>
                      )}

                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" color="text.secondary">
                          QR Code:
                        </Typography>
                        {table.qrCode ? (
                          <Chip label="Available" size="small" color="info" />
                        ) : (
                          <Typography variant="body2" fontSize="0.75rem">-</Typography>
                        )}
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          Created At
                        </Typography>
                        <Typography variant="body2" fontSize="0.875rem">
                          {formatDate(table.createdAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{ 
                overflowX: 'auto',
                '& .MuiTableCell-root': {
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px', sm: '16px' },
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120 }}>Table Number</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Table Name</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Capacity</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Location</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>QR Code</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tables.map((table) => (
                    <TableRow key={table._id} hover>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {table.tableNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {table.tableName || '-'}
                      </TableCell>
                      <TableCell>
                        {table.capacity ? `${table.capacity} seats` : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={table.status}
                          size="small"
                          color={getStatusColor(table.status)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        {table.location || '-'}
                      </TableCell>
                      <TableCell>
                        {table.qrCode ? (
                          <Chip label="Available" size="small" color="info" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(table.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default TablesPage;


